<template>
    <div class="slider-wrapper" ref="sliderWrapper">
      <div class="slides" ref="slides" :style="{
        transform: `translateX(${currentTranslate}px)`,
        transition: dragging ? 'none' : 'transform 300ms ease'
      }">
  
        <!-- Slide 1: Informations principales -->
        <div class="slide">
          <h4>1. Informations principales</h4>
          <b-form-group label="Titre de la tâche">
            <b-form-input v-model="form.title" required />
          </b-form-group>
  
          <b-form-group label="Client">
            <b-form-input 
              v-model="clientSearch"
              placeholder="Tapez pour rechercher un client..."
              @input="filterClients"
              @focus="onClientInputFocusWithReset"
              @blur="onClientInputBlurRestore"
            />
            <div v-if="showClientResults && filteredClientOptions.length > 0" class="client-search-results mt-2">
              <div 
                v-for="client in filteredClientOptions" 
                :key="client.value"
                class="client-option p-2 border-bottom cursor-pointer"
                @click="selectClient(client)"
                :class="{ 'bg-light': form.client === client.value }"
              >
                {{ client.text }}
              </div>
            </div>
          </b-form-group>
  
          <b-form-group label="Utilisateur assigné">
            <b-form-select v-model="form.assigned_user_id" :options="userOptions" />
          </b-form-group>
  
          <b-form-group label="Statut">
            <b-form-select v-model="form.statut">
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminée</option>
              <option value="annulee">Annulée</option>
            </b-form-select>
          </b-form-group>
        </div>
  
        <!-- Slide 2: Mémo -->
        <div class="slide">
          <h4>2. Mémo</h4>
          <b-form-group label="Mémo">
            <div class="quill-editor-container">
              <div ref="quillEditor" class="quill-editor"></div>
            </div>
          </b-form-group>
        </div>
  
        <!-- Slide 3: Photos -->
        <div class="slide">
          <h4>3. Photos</h4>
          <b-button size="sm" variant="outline-primary" @click="openPhotoModal">
            Ajouter / Prendre une photo
          </b-button>
          <div class="d-flex flex-wrap mt-2">
            <div
              v-for="(photo, i) in photoPreviews"
              :key="`photo-${i}`"
              class="photo-thumbnail-wrapper position-relative me-2 mb-2"
              style="width:80px"
            >
              <img :src="photo.src" class="img-thumbnail" style="width:100%" @click="openPreview(photo)" />
              <button class="delete-btn" @click.stop="removePhoto(i)">&times;</button>
            </div>
          </div>
        </div>
  
      </div>
    </div>
  
    <div class="fixed-footer">
      <div class="pagination-dots">
        <span v-for="(_, i) in 3" :key="i" class="dot" :class="{ active: i === step }" />
      </div>
      
      <!-- Indicateur d'envoi en cours -->
      <div v-if="uploadingPhotos" class="uploading-indicator">
        <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
          <span class="visually-hidden">Envoi en cours...</span>
        </div>
        <span class="text-primary">Envoi des photos en cours...</span>
      </div>
      
      <div class="actions d-flex justify-content-between">
        <b-button variant="secondary" :disabled="step === 0" @click="goPrev">&laquo; Précédent</b-button>
        <b-button variant="light" @click="goToHome">Retour</b-button>
        <b-button v-if="step < 2" variant="primary" @click="goNext">Suivant &raquo;</b-button>
        <b-button v-else variant="success" :disabled="loading || uploadingPhotos" @click="submitEdit">
          {{ loading ? 'Enregistrement…' : 'Enregistrer' }}
        </b-button>
      </div>
    </div>
  
    <b-modal v-model="showPhotoModal" title="Ajouter une photo" hide-footer size="lg">
      <b-form>
            <b-form-group label="Nom de la photo">
            <b-form-input v-model="newPhotoName" required />
            </b-form-group>

            <div v-if="errorMsg" class="alert alert-danger py-1 my-2">
            {{ errorMsg }}
            </div>

        <!-- Si ni caméra ni preview -->
            <div v-if="!usingCamera && !modalPreview">
          <b-button class="w-100 mb-3" @click="startCamera">Prendre une photo</b-button>
          <b-form-group label="Sélectionner des fichiers">
            <input type="file" multiple @change="handleFileUpload" class="form-control" accept="image/*" />
          </b-form-group>
            </div>

        <!-- Caméra ouverte -->
            <div v-else-if="usingCamera">
          <!-- Boutons entre le nom et la vidéo -->
          <div class="d-flex justify-content-end mb-3">
                <b-button class="me-2" @click="captureFromCamera">Capturer</b-button>
            <b-button variant="secondary" @click="stopCamera">Annuler</b-button>
            </div>
          
          <!-- Vidéo en dessous -->
          <video autoplay playsinline style="width:100%" ref="modalVideo"></video>
            </div>

        <!-- Preview -->
            <div v-else-if="modalPreview">
          <!-- Boutons entre le nom et la photo -->
          <div class="d-flex justify-content-end mb-3">
            <b-button class="me-2" @click="startCamera">Reprendre la photo</b-button>
            <b-button
                variant="primary"
                type="button"
                :disabled="!newPhotoData"
                @click="addPhoto"
            >
              Ajouter
            </b-button>
          </div>
          
          <!-- Photo en dessous -->
            <div class="text-center my-2">
            <img :src="modalPreview" style="max-width: 100%; max-height: 240px; cursor:pointer"
                 @click="openPreview({ src: modalPreview, nom: newPhotoName })" />
            </div>
            </div>

            <canvas ref="previewCanvas" style="display:none"></canvas>
        </b-form>
        </b-modal>
  
    <b-modal v-model="showPreviewModal" hide-footer title="Aperçu de la photo" size="lg">
      <div v-if="previewPhoto">
        <!-- Formulaire d'édition du nom -->
        <b-form-group label="Nom de la photo" class="mb-3">
          <b-form-input 
            v-model="editingPhotoName" 
            placeholder="Nom de la photo..."
            @keyup.enter="savePhotoName"
          />
        </b-form-group>
        
        <!-- Image en plein écran -->
        <div class="text-center">
          <img :src="previewPhoto.src" class="img-fluid" style="max-height: 60vh;" />
        </div>
        
        <!-- Boutons d'action -->
        <div class="d-flex justify-content-between mt-3">
          <b-button variant="secondary" @click="showPreviewModal = false">
            Fermer
          </b-button>
          <b-button 
            variant="primary" 
            @click="savePhotoName"
            :disabled="!editingPhotoName.trim() || editingPhotoName === previewPhoto.nom"
          >
            Enregistrer le nom
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal de synchronisation -->
    <b-modal v-model="showSyncModal" title="Modifications locales détectées" hide-footer>
      <div class="text-center">
        <p class="mb-4">
          Des modifications locales ont été détectées. Que souhaitez-vous faire ?
        </p>
        
        <div class="d-flex flex-column gap-2">
          <b-button 
            variant="info" 
            @click="showDifferences"
            class="mb-2"
          >
            <i class="fas fa-eye me-2"></i>
            Voir les différences
          </b-button>
          
          <b-button 
            variant="success" 
            @click="handleSyncYes"
            class="mb-2"
          >
            <i class="fas fa-sync-alt me-2"></i>
            Envoyer mes modifications au serveur
          </b-button>
          
          <b-button 
            variant="danger" 
            @click="handleSyncNoCancel"
            class="mb-2"
          >
            <i class="fas fa-times me-2"></i>
            Utiliser les données du serveur (perdre mes modifications)
          </b-button>
          
          <b-button 
            variant="secondary" 
            @click="handleSyncNoContinue"
          >
            <i class="fas fa-edit me-2"></i>
            Continuer à modifier (garder mes modifications locales)
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal des différences -->
    <b-modal v-model="showDiffModal" title="Différences entre serveur et local" size="lg" hide-footer>
      <div class="differences-container">
        <div class="differences-header mb-3">
          <div class="row">
            <div class="col-6">
              <h6 class="text-center text-primary">
                <i class="fas fa-server me-2"></i>Serveur
              </h6>
            </div>
            <div class="col-6">
              <h6 class="text-center text-warning">
                <i class="fas fa-laptop me-2"></i>Local
              </h6>
            </div>
          </div>
        </div>
        
        <div class="differences-content">
          <div v-for="diff in diffData.differences" :key="diff.field" class="difference-item mb-3">
            <div class="difference-field-name">
              <strong>{{ diff.fieldName }}</strong>
            </div>
            <div class="row">
              <div class="col-6">
                <div class="server-value" :class="{ 'empty-value': diff.serverEmpty }">
                  <span v-if="diff.serverEmpty" class="text-muted">— Vide —</span>
                  <span v-else v-html="diff.serverValue"></span>
                </div>
              </div>
              <div class="col-6">
                <div class="local-value" :class="{ 'empty-value': diff.localEmpty }">
                  <span v-if="diff.localEmpty" class="text-muted">— Vide —</span>
                  <span v-else v-html="diff.localValue"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-3">
          <b-button variant="secondary" @click="showDiffModal = false">
            <i class="fas fa-times me-2"></i>Fermer
          </b-button>
        </div>
      </div>
    </b-modal>
  </template>
  
  <script>
  import api from '@/plugins/api';
  import Quill from 'quill';
import localDB from '@/services/localDB.js';
  
  // Variable simple pour l'instance Quill (comme dans EditInterventionPage)
  let quillTaskEditor = null;
  
  /**
   * Page d'édition d'une tâche
   * Permet de modifier les informations d'une tâche existante avec :
   * - Interface multi-slides (3 slides)
   * - Éditeur de texte riche (Quill.js) pour le mémo
   * - Gestion des photos
   * - Sauvegarde automatique
   * - Navigation par swipe sur mobile
   */
  export default {
    name: 'EditTaskPage',
    data() {
      return {
        // Données du formulaire de la tâche
        form: {
          title: '', client: null, assigned_user_id: null,
          statut: 'en_cours', memo: '', photo_ids: []
        },
        quill: null, // Instance de l'éditeur Quill (pour compatibilité)
        
        // Navigation et interface
        taskId: this.$route.params.id,
        step: 0, // Slide actuelle (0, 1, 2)
        currentTranslate: 0, // Position actuelle du slider
        prevTranslate: 0, // Position précédente pour le swipe
        dragging: false, // État du swipe en cours
        startX: 0, // Position de départ du swipe
        animationID: null, // ID de l'animation pour requestAnimationFrame
        
        // Options pour les listes déroulantes / recherche client
        clientOptions: [],
        clientSearch: '',
        filteredClientOptions: [],
        showClientResults: false,
        selectedClientBeforeEdit: null,
        userOptions: [],
        
        // Gestion des photos
        photoPreviews: [],
        newPhotoName: '',
        newPhotoData: null, // Données base64 de la photo
        modalPreview: null, // Aperçu de la photo dans le modal
        showPhotoModal: false, // Affichage du modal d'ajout de photo
        previewPhoto: null, // Photo sélectionnée pour aperçu
        showPreviewModal: false, // Affichage du modal d'aperçu
        
        // Photos temporaires en local
        tempPhotos: [],
        
        // États de l'interface
        loading: false, // État de chargement
        uploadingPhotos: false, // Indicateur d'envoi en cours
        errorMsg: '', // Message d'erreur
        
        // Gestion de la caméra
        modalVideo: null, // Élément vidéo du modal
        usingCamera: false, // Caméra active ou non
        videoStream: null, // Flux vidéo de la caméra
        isSwiping: false, // Évite les swipes multiples

        // Pour l'édition du nom de la photo
        editingPhotoName: '',

        // Variables pour le système de brouillon
        originalData: null, // Données originales pour détecter les changements
        initialFormData: null, // Données initiales pour la comparaison
        lastPatchedData: null, // Dernières données envoyées au serveur

        // Variables pour les modals de synchronisation
        showSyncModal: false, // Modal pour voir les détails d'une intervention
        syncModalData: null, // Données pour la modal de synchronisation

        // Variables pour la modal des différences
        showDiffModal: false,
        diffData: {
          server: {},
          local: {},
          differences: []
        },
      };
    },
    /**
     * Initialisation du composant
     * - Charge les données de la tâche
     * - Configure le swipe pour mobile
     * - Initialise l'éditeur Quill
     * - Configure les événements de la fenêtre
     */
    mounted() {
      this.fetchData(); // Charge les données de la tâche
      this.setupSwipe(); // Configure la navigation par swipe
      this.updateTranslate(); // Initialise la position du slider
      window.addEventListener('resize', this.updateTranslate); // Gestion du redimensionnement
      window.addEventListener('beforeunload', this.handleBeforeUnload); // Sauvegarde avant fermeture
      
      // Initialiser Quill après que les données soient chargées
      this.$nextTick(() => {
        // Attendre un peu plus pour s'assurer que le DOM est complètement rendu
        setTimeout(() => {
        this.initQuill(); // Initialise l'éditeur de texte riche
        }, 100);
      });

      // Watcher pour sauvegarder automatiquement les modifications
      this.$watch('form', async (newValue) => {
        // Ne sauvegarder que si on a des données initiales et qu'il y a eu des modifications
        if (this.taskId && newValue && this.initialFormData) {
          const currentData = JSON.stringify(newValue);
          const initialData = JSON.stringify(this.initialFormData);
          
          // Ne sauvegarder que si les données ont changé par rapport aux données initiales
          if (currentData !== initialData) {
            try {
              await this.saveLocalDraft();
              await this.saveTempPhotos();
              console.log('Sauvegarde locale effectuée (modifications détectées)');
            } catch (error) {
              console.error('Erreur sauvegarde locale:', error);
            }
          }
        }
      }, { deep: true, immediate: false });
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.updateTranslate);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      
      // Pas besoin de nettoyer les event listeners car ils sont gérés automatiquement
    },
    methods: {
      // Mappe le champ client -> contact_id pour l'API backend
      mapTaskPayload(obj) {
        const { client, ...rest } = obj || {};
        return { ...rest, contact_id: client };
      },
      // ==== Recherche client (comme EditInterventionPage) ====
      filterClients() {
        if (!this.clientSearch.trim()) {
          this.filteredClientOptions = this.clientOptions.slice(0, 20);
          return;
        }
        const term = this.clientSearch.toLowerCase();
        this.filteredClientOptions = this.clientOptions
          .filter(c => (c.text || '').toLowerCase().includes(term))
          .slice(0, 10);
      },
      selectClient(client) {
        this.form.client = client.value;
        this.clientSearch = client.text;
        this.filteredClientOptions = [];
        this.showClientResults = false;
      },
      onClientInputFocusWithReset() {
        this.selectedClientBeforeEdit = this.form.client;
        this.clientSearch = '';
        this.filterClients();
        this.showClientResults = true;
      },
      onClientInputBlurRestore() {
        setTimeout(() => {
          this.showClientResults = false;
          if (this.form.client === this.selectedClientBeforeEdit) {
            const client = this.clientOptions.find(c => c.value === this.form.client);
            this.clientSearch = client ? client.text : '';
          }
          this.selectedClientBeforeEdit = null;
        }, 200);
      },
      /**
       * Initialise l'éditeur de texte riche Quill
       * Configure la toolbar avec tous les outils de formatage
       * Synchronise le contenu avec le modèle Vue
       */
      initQuill() {
        // Vérifier que l'élément DOM existe
        if (!this.$refs.quillEditor) {
          console.error('Élément quillEditor non trouvé');
          return;
        }

        // Vérifier que l'élément est visible
        if (this.$refs.quillEditor.offsetParent === null) {
          console.log('Élément quillEditor non visible, attente...');
          setTimeout(() => this.initQuill(), 100);
          return;
        }

        const toolbarOptions = [
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'size': ['small', false, 'large', 'huge'] }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          ['clean']
        ];

        // Configuration personnalisée pour les icônes undo/redo
        const icons = Quill.import('ui/icons');
        icons['undo'] = '↶';
        icons['redo'] = '↷';

        // Détruire l'éditeur existant s'il existe
        if (quillTaskEditor) {
          console.log('🔄 Destruction de l\'éditeur Quill existant');
          this.$refs.quillEditor.innerHTML = '';
          quillTaskEditor = null;
          this.quill = null;
        }

        try {
          console.log('🔄 Création de l\'éditeur Quill');
          // Utiliser une variable simple comme dans EditInterventionPage
          quillTaskEditor = new Quill(this.$refs.quillEditor, {
          theme: 'snow',
          modules: {
              toolbar: toolbarOptions,
              history: {
                delay: 2000,
                maxStack: 500,
                userOnly: true
              }
          },
          placeholder: 'Saisissez votre mémo...'
        });

          // Garder la référence pour compatibilité
          this.quill = quillTaskEditor;

        // Synchroniser avec le modèle Vue à chaque modification
          quillTaskEditor.on('text-change', () => {
            const content = quillTaskEditor.root.innerHTML;
            // Si le contenu est vide ou juste du HTML vide, envoyer une chaîne vide
            if (!content || content === '<p><br></p>' || content === '<p></p>' || content === '<br>') {
              this.form.memo = '';
            } else {
              this.form.memo = content;
            }
        });

        // Charger le contenu existant si disponible
        if (this.form.memo) {
            quillTaskEditor.root.innerHTML = this.form.memo;
          }

          // Créer des boutons undo/redo personnalisés
          setTimeout(() => {
            console.log('🔄 Création des boutons undo/redo personnalisés');
            
            // Créer le conteneur pour les boutons undo/redo
            const undoRedoContainer = document.createElement('div');
            undoRedoContainer.className = 'undo-redo-container';
            undoRedoContainer.style.cssText = 'display: flex; gap: 5px; margin-bottom: 10px; justify-content: center;';
            
            // Bouton Undo
            const undoButton = document.createElement('button');
            undoButton.innerHTML = '↶ Annuler';
            undoButton.className = 'btn btn-outline-secondary btn-sm';
            undoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
            undoButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              try {
                // Utiliser la variable simple comme dans EditInterventionPage
                if (quillTaskEditor && quillTaskEditor.history && quillTaskEditor.history.stack) {
                  const undoStack = quillTaskEditor.history.stack.undo;
                  if (undoStack && undoStack.length > 0) {
                    quillTaskEditor.history.undo();
                    console.log('✅ Undo effectué sur mémo');
                  } else {
                    console.log('⚠️ Aucune action à annuler');
                  }
                }
              } catch (error) {
                console.error('Erreur lors de l\'undo:', error);
              }
            });
            
            // Bouton Redo
            const redoButton = document.createElement('button');
            redoButton.innerHTML = '↷ Rétablir';
            redoButton.className = 'btn btn-outline-secondary btn-sm';
            redoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
            redoButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              try {
                // Utiliser la variable simple comme dans EditInterventionPage
                if (quillTaskEditor && quillTaskEditor.history && quillTaskEditor.history.stack) {
                  const redoStack = quillTaskEditor.history.stack.redo;
                  if (redoStack && redoStack.length > 0) {
                    quillTaskEditor.history.redo();
                    console.log('✅ Redo effectué sur mémo');
                  } else {
                    console.log('⚠️ Aucune action à rétablir');
                  }
                }
              } catch (error) {
                console.error('Erreur lors du redo:', error);
              }
            });
            
            // Ajouter les boutons au conteneur
            undoRedoContainer.appendChild(undoButton);
            undoRedoContainer.appendChild(redoButton);
            
            // Insérer le conteneur avant l'éditeur Quill
            this.$refs.quillEditor.parentNode.insertBefore(undoRedoContainer, this.$refs.quillEditor);
            
            console.log('✅ Boutons undo/redo personnalisés créés');
          }, 100);

          console.log('✅ Éditeur Quill initialisé');
        } catch (error) {
          console.error('Erreur lors de l\'initialisation de Quill:', error);
        }
      },

      /**
       * Charge toutes les données nécessaires :
       * - Informations de la tâche
       * - Photos associées
       * - Liste des clients et utilisateurs
       * Sauvegarde les données originales pour détecter les changements
       */
      async fetchData() {
        try {
        const task = await api.get(`/tasks/${this.taskId}`);
          console.log('Données de la tâche reçues:', task.data);
          
          // Vérifier s'il y a des données locales non synchronisées
          let localDraft = await localDB.getTaskDraft(this.taskId);
          const isLocalUpToDate = await localDB.isTaskLocalUpToDate(this.taskId, task.data.updated_at || task.data.date_creation);
          
          // Vérifier si un patch a été fait avant le refresh
          const patchDone = sessionStorage.getItem(`task_patch_done_${this.taskId}`);
          
          if (patchDone === 'true') {
            console.log('✅ Patch détecté avant refresh, suppression des marqueurs et nettoyage des données locales');
            sessionStorage.removeItem(`task_patch_done_${this.taskId}`);
            sessionStorage.removeItem(`task_cleanup_needed_${this.taskId}`);
            
            // Nettoyer les données locales car le patch a été fait
            try {
              await localDB.cleanTaskData(this.taskId);
              console.log('✅ Données locales nettoyées après patch avant refresh');
            } catch (error) {
              console.error('❌ Erreur nettoyage après patch:', error);
            }
            
            // Utiliser directement les données serveur sans vérifier les données locales
            localDraft = null;
            
            // Forcer l'utilisation des données serveur mises à jour
            console.log('🔄 Utilisation des données serveur après patch');
        Object.assign(this.form, task.data);
        if (Object.prototype.hasOwnProperty.call(task.data, 'contact_id')) {
          this.form.client = task.data.contact_id;
        }
            
            // Mettre à jour les données initiales pour la comparaison
            this.initialFormData = JSON.parse(JSON.stringify({
              title: task.data.title,
              client: task.data.contact_id,
              assigned_user_id: task.data.assigned_user_id,
              statut: task.data.statut,
              memo: task.data.memo,
              photo_ids: [...task.data.photo_ids]
            }));
            
            console.log('📝 Données serveur chargées après patch:', this.form);
          }
          
          if (localDraft && !isLocalUpToDate) {
            // Il y a des données locales plus récentes
            console.log('Données locales détectées, restauration et synchronisation...');
            
            // Restaurer les données locales
            Object.assign(this.form, {
              ...this.form,
              title: localDraft.title || task.data.title,
              client: localDraft.client || task.data.contact_id,
              assigned_user_id: localDraft.assigned_user_id || task.data.assigned_user_id,
              statut: localDraft.statut || task.data.statut,
              memo: localDraft.memo || task.data.memo,
              photo_ids: localDraft.photo_ids || [...task.data.photo_ids]
            });
            
            // Vérifier s'il y a des photos temporaires
            const tempPhotosData = await localDB.getTempTaskPhotos(this.taskId);
            const hasTempPhotos = tempPhotosData.length > 0;
            
            // Afficher l'alerte si il y a des photos temporaires OU si les données sont différentes
            const localDataString = JSON.stringify({
              title: localDraft.title,
              client: localDraft.client,
              assigned_user_id: localDraft.assigned_user_id,
              statut: localDraft.statut,
              memo: localDraft.memo,
              photo_ids: localDraft.photo_ids
            });
            
            const serverDataString = JSON.stringify({
              title: task.data.title,
              client: task.data.contact_id,
              assigned_user_id: task.data.assigned_user_id,
              statut: task.data.statut,
              memo: task.data.memo,
              photo_ids: task.data.photo_ids
            });
            
            const hasDataChanges = localDataString !== serverDataString;
            
            if (hasTempPhotos || hasDataChanges) {
              // Vérifier si on peut faire un patch automatique intelligent
              const canAutoPatch = this.checkIntelligentAutoPatch(task.data, localDraft);
              
              if (canAutoPatch) {
                console.log('🤖 PATCH AUTOMATIQUE INTELLIGENT - Champs serveur vides et champs locaux remplis');
                try {
                  await this.performIntelligentAutoPatch(localDraft);
                  console.log('✅ Patch automatique intelligent réussi');
                  return; // Sortir de la fonction pour éviter d'afficher la modal
                } catch (error) {
                  console.error('❌ Erreur lors du patch automatique intelligent:', error);
                  // Continuer vers la modal de synchronisation en cas d'erreur
                }
              }
              
              console.log('✅ Affichage de la modal de synchronisation');
              // Afficher la modal de synchronisation
              this.showSyncModal = true;
              this.syncModalData = { data: task.data, localDraft };
            } else {
              console.log('Brouillon local détecté mais pas de modifications réelles, nettoyage automatique...');
              await this.cleanLocalDataWithVerification('brouillon sans modifications');
              
              // Vérifier que le nettoyage a fonctionné
              const statusAfterClean = await localDB.checkTaskStatus(this.taskId);
              if (!statusAfterClean || (!statusAfterClean.draft && statusAfterClean.photos.length === 0)) {
                console.log('✅ Nettoyage réussi, pas de modal de synchronisation nécessaire');
                return; // Sortir de la fonction pour éviter d'afficher la modal
              }
            }
            
          } else {
            // Utiliser les données serveur (normales)
            Object.assign(this.form, task.data);
            if (Object.prototype.hasOwnProperty.call(task.data, 'contact_id')) {
              this.form.client = task.data.contact_id;
            }
            
            // Si les données locales sont à jour, les supprimer
            if (localDraft && isLocalUpToDate) {
              await this.cleanLocalDataWithVerification('données à jour');
            }
            
            // Nettoyer les données locales si elles correspondent aux données initiales (pas de vraies modifications)
            if (localDraft && !isLocalUpToDate) {
              const localDataString = JSON.stringify({
                title: localDraft.title,
                client: localDraft.client,
                assigned_user_id: localDraft.assigned_user_id,
                statut: localDraft.statut,
                memo: localDraft.memo,
                photo_ids: localDraft.photo_ids
              });
              
              const serverDataString = JSON.stringify({
                title: task.data.title,
                client: task.data.contact_id,
                assigned_user_id: task.data.assigned_user_id,
                statut: task.data.statut,
                memo: task.data.memo,
                photo_ids: task.data.photo_ids
              });
              
              // Vérifier s'il y a des photos temporaires
              const tempPhotosData = await localDB.getTempTaskPhotos(this.taskId);
              const hasTempPhotos = tempPhotosData.length > 0;
              
              if (localDataString === serverDataString && !hasTempPhotos) {
                console.log('Données locales identiques aux données serveur et pas de photos temporaires, nettoyage...');
                await this.cleanLocalDataWithVerification('données identiques');
              } else if (localDataString === serverDataString && hasTempPhotos) {
                console.log('Données identiques mais photos temporaires présentes, affichage de l\'alerte...');
                // L'alerte sera affichée plus tard quand les photos temporaires seront chargées
              }
            }
          }
        
        // Sauvegarder les données originales pour comparaison
        this.originalData = JSON.parse(JSON.stringify({
          title: this.form.title,
          client: this.form.client,
          assigned_user_id: this.form.assigned_user_id,
          statut: this.form.statut,
          memo: this.form.memo,
          photo_ids: [...this.form.photo_ids]
        }));
          
          // Charger les photos
        const photos = await api.get('/photos-tasks');
        this.photoPreviews = photos.data
          .filter(p => this.form.photo_ids.includes(p.id))
          .map(p => ({ id: p.id, nom: p.nom, src: `data:image/png;base64,${p.image}` }));
          
          // Charger les options
        const clients = await api.get('/tasks/contacts');
        this.clientOptions = [{ value: null, text: '(Aucun)' }, ...clients.data.map(c => ({ value: c.codechrono, text: c.nom_affichage }))];
        // Initialiser la liste filtrée et synchroniser l'input si un client est déjà défini
        this.filteredClientOptions = this.clientOptions.slice(0, 20);
        if (this.form.client != null) {
          const client = this.clientOptions.find(c => c.value === this.form.client);
          if (client) this.clientSearch = client.text;
        }
        const users = await api.get('/utilisateurs');
        this.userOptions = [{ value: null, text: '(Aucun)' }, ...users.data.map(u => ({ value: u.id, text: u.name }))];
          
          // Réinitialiser les photos temporaires
          this.tempPhotos = [];
          
          // Vérifier s'il y a des photos temporaires dans la DB locale
          console.log('Vérification des photos temporaires dans la DB locale...');
          const tempPhotosData = await localDB.getTempTaskPhotos(this.taskId);
          
          console.log('Photos temporaires trouvées dans la DB:', {
            photos: tempPhotosData.length,
            localDraft: !!localDraft,
            isLocalUpToDate: isLocalUpToDate
          });
          
          // Restaurer les photos temporaires depuis la DB locale
          if (tempPhotosData.length > 0) {
            console.log('Restoration des photos temporaires depuis la DB locale...');
            tempPhotosData.forEach(photoData => {
              const tempPhoto = {
                id: photoData.temp_id,
                nom: photoData.photo_name,
                image: photoData.photo_data,
                dataUrl: `data:image/png;base64,${photoData.photo_data}`
              };
              
              this.tempPhotos.push(tempPhoto);
              this.photoPreviews.push({ 
                id: tempPhoto.id, 
                nom: tempPhoto.nom, 
                src: tempPhoto.dataUrl 
              });
            });
            
            console.log('Photos temporaires restaurées:', this.tempPhotos.length);
          }
        
        // Charger le contenu dans Quill si déjà initialisé
          if (quillTaskEditor && this.form.memo) {
            try {
              quillTaskEditor.root.innerHTML = this.form.memo;
            } catch (error) {
              console.error('Erreur lors du chargement du contenu dans Quill:', error);
            }
          }
          
          // Initialiser lastPatchedData avec les données actuelles
          this.lastPatchedData = JSON.parse(JSON.stringify(this.form));
          
          // Définir les données initiales pour la comparaison (après chargement complet)
          this.initialFormData = JSON.parse(JSON.stringify(this.form));
          console.log('Données initiales définies pour la comparaison');
          
        } catch (err) {
          console.error('Erreur lors du chargement des données:', err);
          alert('Erreur lors du chargement des données. Veuillez réessayer.');
        }
      },

      /**
       * Configure la navigation par swipe pour mobile
       * Permet de naviguer entre les slides en glissant le doigt
       * Utilise requestAnimationFrame pour des animations fluides
       */
      setupSwipe() {
        const wrapper = this.$refs.sliderWrapper;
        
        wrapper.addEventListener('touchstart', e => {
          this.startX = e.touches[0].clientX;
          this.dragging = true;
          this.animationID = requestAnimationFrame(() => {
            this.animate();
          });
        });
        
        wrapper.addEventListener('touchmove', e => {
          if (!this.dragging) return;
          e.preventDefault(); // Empêche le scroll pendant le swipe
          this.currentTranslate = this.prevTranslate + (e.touches[0].clientX - this.startX);
        });
        
        wrapper.addEventListener('touchend', () => {
          this.dragging = false;
          if (this.animationID) {
            cancelAnimationFrame(this.animationID);
            this.animationID = null;
          }
          
          const moved = this.currentTranslate - this.prevTranslate;
          let changed = false;
          
          if (moved < -50 && this.step < 2) {
            this.step++;
            changed = true;
          }
          if (moved > 50 && this.step > 0) {
            this.step--;
            changed = true;
          }
          
          this.updateTranslate();
          
          if (changed) {
            this.autoSaveTask().catch(err => {
              console.error("Erreur lors de la sauvegarde automatique:", err);
            });
          }
        });
      },

      /**
       * Animation fluide du swipe
       * Utilise requestAnimationFrame pour des performances optimales
       */
      animate() {
        if (this.dragging && this.animationID && this.$refs.sliderWrapper) {
          const slides = this.$refs.sliderWrapper.querySelector('.slides');
          if (slides) {
            slides.style.transform = `translateX(${this.currentTranslate}px)`;
          }
          this.animationID = requestAnimationFrame(() => {
            this.animate();
          });
        }
      },

      /**
       * Patch automatique lors du changement de slide
       * Envoie les modifications au serveur et nettoie le brouillon si réussi
       */
      async autoPatch() {
        console.log("🚀 autoPatch() appelée");
        
        // Vérifier s'il y a des changements
        const hasPhotoChanges = this.tempPhotos.length > 0;
        const hasOtherChanges = this.hasUnsavedChanges();
        
        console.log("📊 État des changements:", {
          hasPhotoChanges,
          hasOtherChanges,
          tempPhotosCount: this.tempPhotos.length,
          hasUnsavedChanges: this.hasUnsavedChanges()
        });
        
        if (!hasOtherChanges && !hasPhotoChanges) {
          console.log("Aucun changement détecté, pas de patch nécessaire");
          return;
        }

        console.log("🔄 Patch automatique en cours...");
        
        try {
          // Envoyer les photos temporaires restantes (si il y en a)
          const newPhotoIds = [];

          if (this.tempPhotos.length > 0) {
            console.log("Envoi des photos temporaires...");
            for (const tempPhoto of this.tempPhotos) {
              try {
                const { data } = await api.post('/photos-tasks', {
                  nom: tempPhoto.nom,
                  image: tempPhoto.image
                });
                newPhotoIds.push(data.id);
              } catch (e) {
                console.error('Erreur envoi photo:', e);
                throw new Error(`Erreur lors de l'envoi de la photo "${tempPhoto.nom}": ${e.message || 'Erreur serveur'}`);
              }
            }
          }

          // Combiner les IDs existants avec les nouveaux
          const allPhotoIds = [...this.form.photo_ids, ...newPhotoIds];

          const payload = {
            ...this.mapTaskPayload(this.form),
            photo_ids: allPhotoIds
          };

          console.log("Envoi PATCH automatique :", payload);

          await api.patch(`/tasks/${this.taskId}`, payload);
          
          // Marquer le patch comme fait pour éviter les conflits lors du refresh
          sessionStorage.setItem(`task_patch_done_${this.taskId}`, 'true');
          
          // Vider les photos temporaires après sauvegarde réussie
          this.tempPhotos = [];
          
          // Mettre à jour les données originales après sauvegarde réussie
          this.originalData = JSON.parse(JSON.stringify({
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: allPhotoIds
          }));
          
          // Mettre à jour les données initiales pour la comparaison
          this.initialFormData = JSON.parse(JSON.stringify({
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: allPhotoIds
          }));
          
          console.log("📝 Données locales mises à jour après patch réussi");
          console.log("📊 Nouvel état initial:", this.initialFormData);
          
          // Nettoyer les données locales après sauvegarde réussie
          try {
            console.log("🧹 Début du nettoyage des données locales...");
            await this.cleanLocalDataWithVerification('patch automatique réussi');
            console.log("✅ Patch automatique réussi, brouillon nettoyé");
            
            // Vérifier que le nettoyage a bien fonctionné
            const checkStatus = await localDB.checkTaskStatus(this.taskId);
            console.log("📊 Statut après nettoyage:", checkStatus);
          } catch (error) {
            console.error('Erreur nettoyage données locales:', error);
          }
          
        } catch (e) {
          console.error('Erreur lors du patch automatique:', e);
          
          // En cas d'erreur, sauvegarder en local pour ne pas perdre les modifications
          await this.saveLocalDraft();
          await this.saveTempPhotos();
          console.log("💾 Modifications sauvegardées en local suite à l'erreur de patch");
          
          // Afficher l'erreur à l'utilisateur
          const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la sauvegarde automatique';
          alert(`Erreur de sauvegarde automatique: ${errorMessage}\n\nLes modifications ont été sauvegardées en local.`);
        }
      },

      /**
       * Navigation vers la slide suivante
       * Patch automatique avant de changer de slide
       */
      async goNext() {
        if (this.step < 2) {
          // Patch automatique avant de passer à la slide suivante
          await this.autoPatch();
          this.step++;
        this.updateTranslate();
        }
      },

      /**
       * Navigation vers la slide précédente
       * Patch automatique avant de changer de slide
       */
      async goPrev() {
        if (this.step > 0) {
          // Patch automatique avant de passer à la slide précédente
          await this.autoPatch();
          this.step--;
        this.updateTranslate();
        }
      },

      /**
       * Met à jour la position du slider
       * Applique la transformation CSS pour afficher la bonne slide
       */
      updateTranslate() {
        if (!this.$refs.sliderWrapper) return; // Vérification de nullité
        
        this.prevTranslate = -this.step * this.$refs.sliderWrapper.clientWidth;
        this.currentTranslate = this.prevTranslate;
        // Correction : applique le style transform immédiatement
        const slides = this.$refs.sliderWrapper.querySelector('.slides');
        if (slides) {
          slides.style.transform = `translateX(${this.currentTranslate}px)`;
        }
      },

      /**
       * Génère un nom par défaut pour les photos
       */
      generateDefaultPhotoName() {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const dateStr = `${day}-${month}-${year}`;
        
        // Récupérer le nom d'utilisateur depuis le localStorage
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          const username = user?.username || user?.nom || 'utilisateur';
          return `${username}_${dateStr}`;
        } catch (e) {
          return `utilisateur_${dateStr}`;
        }
      },

      /**
       * Ouvre le modal d'ajout de photo
       * Réinitialise les données du modal
       */
      openPhotoModal() {
        this.newPhotoData = null;
        this.newPhotoName = this.generateDefaultPhotoName();
        this.modalPreview = null;
        this.errorMsg = '';
        this.showPhotoModal = true;
      },

      /**
       * Gère l'upload d'un fichier photo
       * Convertit le fichier en base64 pour l'envoi
       */
      handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => {
          this.newPhotoData = ev.target.result.split(',')[1];
          this.modalPreview = ev.target.result;
        };
        reader.readAsDataURL(file);
      },

      /**
       * Ajoute une nouvelle photo à la tâche
       * Stocke temporairement en local
       */
      async addPhoto() {
        if (!this.newPhotoData) {
          this.errorMsg = 'Aucune photo sélectionnée';
          return;
        }
        
        try {
          // Stocker temporairement en local
          const tempPhoto = {
            id: `temp_${Date.now()}_${Math.random()}`, // ID temporaire unique
            nom: this.newPhotoName || 'photo',
            image: this.newPhotoData, // Base64 sans le préfixe
            dataUrl: `data:image/png;base64,${this.newPhotoData}` // Garder la dataUrl pour l'affichage
          };

          this.tempPhotos.push(tempPhoto);
          this.photoPreviews.push({ 
            id: tempPhoto.id, 
            nom: tempPhoto.nom, 
            src: tempPhoto.dataUrl 
          });

        this.showPhotoModal = false;
          this.newPhotoName = '';
          this.newPhotoData = null;
          this.modalPreview = null;
          this.errorMsg = '';

          // Sauvegarder les photos temporaires
          await this.saveTempPhotos();
        } catch (err) {
          console.error('Erreur lors de l\'ajout de la photo:', err);
          this.errorMsg = 'Erreur lors de l\'ajout de la photo. Veuillez réessayer.';
        }
              },

      /**
       * Supprime une photo de la tâche
       * Distingue entre photos temporaires et photos existantes
       */
      async removePhoto(i) {
        const photoToRemove = this.photoPreviews[i];
        const tempPhoto = this.tempPhotos.find(p => p.id === photoToRemove.id);
        
        if (tempPhoto) {
          // Photo temporaire - suppression locale
          const confirmMsg = "Supprimer cette photo ?";
          if (!confirm(confirmMsg)) return;
          
          // Supprimer de la liste temporaire
          const tempIndex = this.tempPhotos.findIndex(p => p.id === photoToRemove.id);
          if (tempIndex > -1) {
            this.tempPhotos.splice(tempIndex, 1);
          }
          
          // Supprimer de l'affichage
          this.photoPreviews.splice(i, 1);
        } else {
          // Photo existante - suppression côté serveur
          const confirmDelete = confirm(`Supprimer définitivement la photo "${photoToRemove.nom}" ?`);
            if (!confirmDelete) return;

            try {
                // Supprime le lien ET la photo de la base
            await api.delete(`/photos-tasks/${photoToRemove.id}`);
                this.photoPreviews.splice(i, 1);
            this.form.photo_ids = this.form.photo_ids.filter(id => id !== photoToRemove.id);
                
                // Met à jour la tâche avec la liste mise à jour des photos
                await api.patch(`/tasks/${this.taskId}`, { photo_ids: this.form.photo_ids });
            } catch (err) {
                console.error('Erreur suppression photo :', err);
                alert('Erreur lors de la suppression de la photo.');
          }
            }
            },

      /**
       * Ouvre l'aperçu d'une photo
       * Affiche la photo en plein écran
       */
      openPreview(photo) {
        this.previewPhoto = photo;
        this.editingPhotoName = photo.nom; // Initialiser le nom pour l'édition
        this.showPreviewModal = true;
      },
      
      /**
       * Soumet les modifications finales de la tâche
       * Vérifie les changements et envoie les photos temporaires restantes
       */
      async submitEdit() {
        if (!this.form.title.trim()) {
          alert('Le titre de la tâche est obligatoire.');
          return;
        }
        
        this.loading = true;
        
        try {
          // Vérifier s'il y a eu des changements
          const hasPhotoChanges = this.tempPhotos.length > 0;
          const hasOtherChanges = this.hasUnsavedChanges();
          
          if (!hasOtherChanges && !hasPhotoChanges) {
            console.log("Aucun changement détecté, redirection directe");
            this.$router.push({ name: 'Tasks' });
            return;
          }

          // Envoyer les photos temporaires restantes (si il y en a)
          const newPhotoIds = [];

          if (this.tempPhotos.length > 0) {
            for (const tempPhoto of this.tempPhotos) {
              try {
                const { data } = await api.post('/photos-tasks', {
                  nom: tempPhoto.nom,
                  image: tempPhoto.image
                });
                newPhotoIds.push(data.id);
        } catch (e) {
                console.error('Erreur envoi photo:', e);
                throw new Error(`Erreur lors de l'envoi de la photo "${tempPhoto.nom}": ${e.message || 'Erreur serveur'}`);
              }
            }
          }

          // Combiner les IDs existants avec les nouveaux
          const allPhotoIds = [...this.form.photo_ids, ...newPhotoIds];

          const payload = {
            ...this.mapTaskPayload(this.form),
            photo_ids: allPhotoIds
          };

          console.log("Envoi PATCH final :", payload);

          await api.patch(`/tasks/${this.taskId}`, payload);
          
          // Marquer le patch comme fait pour éviter les conflits lors du refresh
          sessionStorage.setItem(`task_patch_done_${this.taskId}`, 'true');
          
          // Vider les photos temporaires après sauvegarde réussie
          this.tempPhotos = [];
          
          // Nettoyer le brouillon des photos temporaires dans la DB locale
          try {
            await localDB.deleteTempTaskPhotos(this.taskId);
            console.log("📸 Brouillon des photos temporaires nettoyé après sauvegarde");
          } catch (error) {
            console.error('❌ Erreur nettoyage brouillon photos:', error);
          }
          
          // Mettre à jour les données originales après sauvegarde réussie
          this.originalData = JSON.parse(JSON.stringify({
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: allPhotoIds
          }));
          
          // Nettoyer les données locales après sauvegarde réussie
          try {
            await this.cleanLocalDataWithVerification('sauvegarde réussie');
          } catch (error) {
            console.error('Erreur nettoyage données locales:', error);
          }
          
          this.$router.push({ name: 'Tasks' });
        } catch (e) {
          console.error('Erreur lors de la sauvegarde:', e);
          
          // Afficher l'erreur à l'utilisateur
          const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la sauvegarde de la tâche';
          alert(`Erreur: ${errorMessage}\n\nLa tâche n'a pas été sauvegardée. Veuillez réessayer.`);
          
          // Ne pas rediriger vers la page tasks en cas d'erreur
          // L'utilisateur reste sur la page d'édition pour pouvoir corriger et réessayer
        } finally {
            this.loading = false;
        }
        },
        
      cancelEdit() {
        console.log("❌ Annulation - retour sans patch ni nettoyage du brouillon");
        this.$router.push({ name: 'Tasks' });
      },

      /**
       * Démarre la caméra pour prendre une photo
       * Gère les erreurs de compatibilité navigateur
       */
      async startCamera() {
        console.log("startCamera called"); // Vérification
        this.modalPreview = null;
        
        // Vérification de la compatibilité
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          alert('Votre navigateur ne supporte pas l\'accès à la caméra. Veuillez utiliser un navigateur moderne ou activer HTTPS.');
          return;
        }
        
        try {
            this.usingCamera = true;
            const stream = await navigator.mediaDevices.getUserMedia({
            video: {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                facingMode: { ideal: 'environment' }
            }
            });
            this.videoStream = stream;
            const video = this.$refs.modalVideo;
            if (video) video.srcObject = stream;
        } catch (err) {
            console.error("Erreur caméra :", err);
            this.usingCamera = false;
            
            // Messages d'erreur plus spécifiques
            if (err.name === 'NotAllowedError') {
              alert('Accès à la caméra refusé. Veuillez autoriser l\'accès à la caméra dans les paramètres de votre navigateur.');
            } else if (err.name === 'NotFoundError') {
              alert('Aucune caméra trouvée sur votre appareil.');
            } else if (err.name === 'NotSupportedError') {
              alert('Votre navigateur ne supporte pas l\'accès à la caméra. Veuillez utiliser HTTPS.');
            } else {
              alert('Erreur lors de l\'accès à la caméra : ' + err.message);
            }
        }
    },
    stopCamera() {
        this.usingCamera = false;
        if (this.videoStream) {
            this.videoStream.getTracks().forEach(track => track.stop());
            this.videoStream = null;
        }
    },
    captureFromCamera() {
        const video = this.$refs.modalVideo;
        const canvas = this.$refs.previewCanvas;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        this.modalPreview = canvas.toDataURL('image/png');
        this.newPhotoData = this.modalPreview.split(',')[1];
        this.stopCamera();
    },
          async goToHome() {
        // Retour direct sans patch ni nettoyage du brouillon
        console.log("🏠 Retour à la page des tâches sans patch");
        this.$router.push('/tasks');
      },
      
      // Patch obligatoire quand on quitte la page (rafraîchissement/fermeture)
      handleBeforeUnload() {
        console.log("🔄 Patch obligatoire avant fermeture/rafraîchissement");
        
        // Utiliser XMLHttpRequest pour un appel synchrone (nécessaire pour beforeunload)
        const hasPhotoChanges = this.tempPhotos.length > 0;
        const hasOtherChanges = this.hasUnsavedChanges();
        
        console.log("📊 État avant fermeture:", { hasPhotoChanges, hasOtherChanges });
        
        // Toujours faire un patch, même s'il n'y a pas de changements détectés
        try {
          // Envoyer les photos temporaires d'abord
          if (this.tempPhotos.length > 0) {
            console.log("📸 Envoi des photos temporaires avant fermeture...");
            for (const tempPhoto of this.tempPhotos) {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${process.env.VUE_APP_API_URL}/photos-tasks`, false); // false = synchrone
              xhr.setRequestHeader('Content-Type', 'application/json');
              xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
              
              const photoData = {
                nom: tempPhoto.nom,
                image: tempPhoto.image
              };
              
              xhr.send(JSON.stringify(photoData));
              
              if (xhr.status === 201) {
                const response = JSON.parse(xhr.responseText);
                this.form.photo_ids.push(response.id);
              }
            }
          }
          
          // Envoyer le patch de la tâche
          const payload = {
            ...this.mapTaskPayload(this.form),
            photo_ids: [...this.form.photo_ids]
          };
          
          console.log("📤 Envoi du patch avant fermeture:", payload);
          
          const xhr = new XMLHttpRequest();
          xhr.open('PATCH', `${process.env.VUE_APP_API_URL}/tasks/${this.taskId}`, false);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
          
          xhr.send(JSON.stringify(payload));
          
          if (xhr.status === 200) {
            // Marquer le patch comme fait
            sessionStorage.setItem(`task_patch_done_${this.taskId}`, 'true');
            console.log("✅ Patch obligatoire réussi avant fermeture");
            
            // Nettoyer le brouillon des photos temporaires (si des photos ont été envoyées)
            if (this.tempPhotos.length > 0) {
              try {
                // Note: On ne peut pas faire d'appel asynchrone dans beforeunload
                // Le nettoyage sera fait au prochain chargement de la page
                console.log("📸 Photos temporaires envoyées, nettoyage au prochain chargement");
              } catch (error) {
                console.error('❌ Erreur nettoyage photos temporaires:', error);
              }
            }
          } else {
            console.error("❌ Échec du patch avant fermeture, status:", xhr.status);
          }
        } catch (error) {
          console.error('Erreur patch obligatoire avant fermeture:', error);
          // En cas d'erreur, sauvegarder en local
          this.saveLocalDraft();
          this.saveTempPhotos();
        }
      },
      
      // Vérifie si le formulaire a des modifications
      /**
       * Enregistre le nom de la photo sélectionnée
       * Met à jour la photo dans la liste des photos prévisualisées
       * Si c'est une photo existante, met à jour le nom côté serveur
       */
      async savePhotoName() {
        if (!this.previewPhoto || !this.editingPhotoName.trim()) return;
        
        const newName = this.editingPhotoName.trim();
        
        // Vérifier si c'est une photo temporaire (ID commence par "temp_")
        const isTempPhoto = this.previewPhoto.id.toString().startsWith('temp_');
        
        if (isTempPhoto) {
          // Photo temporaire - mise à jour locale
          const tempPhoto = this.tempPhotos.find(p => p.id === this.previewPhoto.id);
          if (tempPhoto) {
            tempPhoto.nom = newName;
          }
        } else {
          // Photo existante - mise à jour côté serveur
          try {
            await api.patch(`/photos-tasks/${this.previewPhoto.id}`, {
              nom: newName
            });
          } catch (error) {
            console.error('Erreur lors de la mise à jour du nom:', error);
            alert('Erreur lors de la mise à jour du nom de la photo');
            return;
          }
        }
        
        // Mettre à jour l'affichage
        const updatedPhoto = { ...this.previewPhoto, nom: newName };
        const index = this.photoPreviews.findIndex(p => p.id === updatedPhoto.id);
        if (index !== -1) {
          this.photoPreviews.splice(index, 1, updatedPhoto);
        }
        
        this.showPreviewModal = false;
        this.editingPhotoName = '';
      },

      // === MÉTHODES POUR LE SYSTÈME DE BROUILLON ===

      // Vérifie s'il y a des modifications non sauvegardées
      hasUnsavedChanges() {
        if (!this.initialFormData) {
          console.log("❌ initialFormData est null, pas de changements détectés");
          return false;
        }
        
        // Comparer chaque champ avec les données initiales
        const titleChanged = this.form.title !== this.initialFormData.title;
        const clientChanged = this.form.client !== this.initialFormData.client;
        const assignedUserChanged = this.form.assigned_user_id !== this.initialFormData.assigned_user_id;
        const statutChanged = this.form.statut !== this.initialFormData.statut;
        const memoChanged = this.form.memo !== this.initialFormData.memo;
        const photoIdsChanged = JSON.stringify(this.form.photo_ids.sort()) !== JSON.stringify(this.initialFormData.photo_ids.sort());
        
        const hasChanges = titleChanged || clientChanged || assignedUserChanged || statutChanged || memoChanged || photoIdsChanged;
        
        console.log("🔍 Vérification des changements:", {
          titleChanged,
          clientChanged,
          assignedUserChanged,
          statutChanged,
          memoChanged,
          photoIdsChanged,
          hasChanges
        });
        
        if (hasChanges) {
          console.log("⚠️ Changements détectés - patch nécessaire");
        } else {
          console.log("✅ Aucun changement détecté - pas de patch nécessaire");
        }
        
        return hasChanges;
      },

      // Sauvegarde automatique de la tâche
      async autoSaveTask() {
        const hasPhotoChanges = this.tempPhotos.length > 0;
        
        if (!this.hasUnsavedChanges() && !hasPhotoChanges) return;
        
        // Afficher l'indicateur d'envoi si il y a des photos à envoyer
        if (hasPhotoChanges) {
          this.uploadingPhotos = true;
        }
        
        try {
          // Envoyer d'abord les photos temporaires si il y en a
          const newPhotoIds = [];

          if (this.tempPhotos.length > 0) {
            console.log("Envoi des photos temporaires...");
            for (const tempPhoto of this.tempPhotos) {
              try {
                const { data } = await api.post('/photos-tasks', {
                  nom: tempPhoto.nom,
                  image: tempPhoto.image
                });
                newPhotoIds.push(data.id);
              } catch (e) {
                console.error('Erreur envoi photo:', e);
                throw new Error(`Erreur lors de l'envoi de la photo "${tempPhoto.nom}": ${e.message || 'Erreur serveur'}`);
              }
            }
          }

          // Mettre à jour les IDs des photos après envoi réussi
          if (newPhotoIds.length > 0) {
            this.form.photo_ids.push(...newPhotoIds);
            this.tempPhotos = []; // Vider les photos temporaires
            
            // Nettoyer le brouillon des photos temporaires dans la DB locale
            try {
              await localDB.deleteTempTaskPhotos(this.taskId);
              console.log("📸 Brouillon des photos temporaires nettoyé après envoi réussi");
            } catch (error) {
              console.error('❌ Erreur nettoyage brouillon photos:', error);
            }
          }

          await api.patch(`/tasks/${this.taskId}`, this.mapTaskPayload(this.form));
          console.log('Tâche sauvegardée automatiquement');
          
          // Mettre à jour les données originales après sauvegarde
          this.originalData = JSON.parse(JSON.stringify({
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: [...this.form.photo_ids]
          }));
          
          // Mettre à jour les données initiales pour la comparaison
          this.initialFormData = JSON.parse(JSON.stringify({
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: [...this.form.photo_ids]
          }));
          
          // Nettoyer les données locales après sauvegarde réussie
          try {
            await this.cleanLocalDataWithVerification('patch automatique réussi');
            console.log("✅ Brouillon nettoyé après patch automatique");
          } catch (error) {
            console.error('❌ Erreur nettoyage brouillon après patch automatique:', error);
          }
        } catch (err) {
          console.error('Erreur lors de la sauvegarde automatique:', err);
          // Afficher l'erreur à l'utilisateur
          const errorMessage = err.message || err.response?.data?.error || 'Erreur lors de la sauvegarde automatique';
          alert(`Erreur: ${errorMessage}\n\nLes modifications n'ont pas été sauvegardées automatiquement.`);
        } finally {
          // Masquer l'indicateur d'envoi
          this.uploadingPhotos = false;
        }
      },

      // Nettoyer les données locales avec vérification
      async cleanLocalDataWithVerification(reason) {
        console.log(`🧹 Nettoyage des données locales (${reason})`);
        try {
          // Vérifier le statut avant nettoyage
          const statusBefore = await localDB.checkTaskStatus(this.taskId);
          console.log("📊 Statut avant nettoyage:", statusBefore);
          
          await localDB.cleanTaskData(this.taskId);
          console.log('✅ Nettoyage réussi');
          
          // Vérifier le statut après nettoyage
          const statusAfter = await localDB.checkTaskStatus(this.taskId);
          console.log("📊 Statut après nettoyage:", statusAfter);
        } catch (error) {
          console.error('❌ Erreur lors du nettoyage:', error);
        }
      },

      // Fonction pour vérifier si on peut faire un patch automatique intelligent
      checkIntelligentAutoPatch(serverData, localDraft) {
        console.log('🔍 Vérification du patch automatique intelligent...');
        console.log('🔍 Fonction checkIntelligentAutoPatch appelée avec:', { serverData, localDraft });
        
        // Fonction helper pour vérifier si un champ est "vide"
        const isEmpty = (value) => {
          if (value === null || value === undefined || value === '') return true;
          if (typeof value === 'string' && value.trim() === '') return true;
          if (typeof value === 'string' && (value === '<p><br></p>' || value === '<p></p>' || value === '<br>')) return true;
          if (typeof value === 'object' && Object.keys(value).length === 0) return true;
          return false;
        };
        
        // Fonction helper pour vérifier si un champ est "rempli"
        const isFilled = (value) => {
          if (isEmpty(value)) return false;
          if (typeof value === 'string' && value.trim() === '') return false;
          return true;
        };
        
        // Comparer les champs principaux
        const comparisons = [
          { field: 'title', serverValue: serverData.title, localValue: localDraft.title, name: 'Titre' },
          { field: 'client', serverValue: serverData.client, localValue: localDraft.client, name: 'Client' },
          { field: 'assigned_user_id', serverValue: serverData.assigned_user_id, localValue: localDraft.assigned_user_id, name: 'Utilisateur assigné' },
          { field: 'statut', serverValue: serverData.statut, localValue: localDraft.statut, name: 'Statut' },
          { field: 'memo', serverValue: serverData.memo, localValue: localDraft.memo, name: 'Mémo' }
        ];
        
        let canAutoPatch = false;
        let autoPatchFields = [];
        
        console.log('🔍 Début de l\'analyse des champs...');
        
        for (const comp of comparisons) {
          const serverEmpty = isEmpty(comp.serverValue);
          const localFilled = isFilled(comp.localValue);
          
          console.log(`🔍 Comparaison ${comp.name}:`, {
            serverValue: comp.serverValue,
            localValue: comp.localValue,
            serverEmpty: serverEmpty,
            localFilled: localFilled
          });
          
          if (serverEmpty && localFilled) {
            console.log(`✅ ${comp.name}: Serveur vide, local rempli - OK pour auto-patch`);
            autoPatchFields.push(comp.field);
            canAutoPatch = true;
            console.log(`🔍 canAutoPatch mis à true pour ${comp.name}, autoPatchFields:`, autoPatchFields);
          } else if (!serverEmpty && !localFilled) {
            console.log(`✅ ${comp.name}: Même état (vide ou rempli) - OK`);
          } else if (!serverEmpty && localFilled) {
            console.log(`⚠️ ${comp.name}: Serveur rempli, local rempli - Vérification nécessaire`);
            // On continue l'analyse au lieu de s'arrêter immédiatement
          } else if (serverEmpty && !localFilled) {
            console.log(`✅ ${comp.name}: Serveur vide, local vide - OK`);
          }
        }
        
        console.log('🔍 Arrivé à la fin de la fonction checkIntelligentAutoPatch');
        console.log('🔍 État final - canAutoPatch:', canAutoPatch, 'autoPatchFields:', autoPatchFields);
        
        if (canAutoPatch) {
          console.log('🤖 PATCH AUTOMATIQUE INTELLIGENT AUTORISÉ pour les champs:', autoPatchFields);
          console.log('✅ Patch automatique possible même avec certains conflits');
        } else {
          console.log('❌ PATCH AUTOMATIQUE INTELLIGENT REFUSÉ - Aucun champ ne peut être patché automatiquement');
        }
        
        console.log('🔍 Résultat final de checkIntelligentAutoPatch:', canAutoPatch);
        return canAutoPatch;
      },

      // Fonction pour effectuer le patch automatique intelligent
      async performIntelligentAutoPatch(localDraft) {
        console.log('🚀 Début du patch automatique intelligent...');
        
        try {
          const payload = {};
          
          if (localDraft.title && localDraft.title.trim()) {
            payload.title = localDraft.title;
            console.log('📝 Ajout du titre au patch automatique');
          }
          
          if (localDraft.client !== null && localDraft.client !== undefined) {
            payload.client = localDraft.client;
            console.log('📝 Ajout du client au patch automatique');
          }
          
          if (localDraft.assigned_user_id !== null && localDraft.assigned_user_id !== undefined) {
            payload.assigned_user_id = localDraft.assigned_user_id;
            console.log('📝 Ajout de l\'utilisateur assigné au patch automatique');
          }
          
          if (localDraft.statut && localDraft.statut.trim()) {
            payload.statut = localDraft.statut;
            console.log('📝 Ajout du statut au patch automatique');
          }
          
          if (localDraft.memo && localDraft.memo.trim() && localDraft.memo !== '<p><br></p>' && localDraft.memo !== '<p></p>' && localDraft.memo !== '<br>') {
            payload.memo = localDraft.memo;
            console.log('📝 Ajout du mémo au patch automatique');
          } else if (localDraft.memo === '<p><br></p>' || localDraft.memo === '<p></p>' || localDraft.memo === '<br>' || !localDraft.memo || localDraft.memo.trim() === '') {
            payload.memo = '';
            console.log('📝 Ajout du mémo vide au patch automatique');
          }
          
          console.log('📦 Payload final pour le patch automatique:', payload);
          
          const response = await api.patch(`/tasks/${this.taskId}`, payload);
          console.log('✅ Patch automatique réussi:', response.data);
          
          // Nettoyer les données locales après patch réussi
          await this.cleanLocalDataWithVerification('patch automatique intelligent');
          
          // Recharger la page pour afficher les données mises à jour
          console.log('🔄 Rechargement de la page...');
          window.location.reload();
          
          return response;
        } catch (error) {
          console.error('❌ Erreur lors du patch automatique intelligent:', error);
          throw error;
        }
      },

      // Fonction pour afficher les différences
      async showDifferences() {
        console.log('🔍 Affichage des différences entre serveur et local...');
        
        const { data } = this.syncModalData;
        const localDraft = await localDB.getTaskDraft(this.taskId);
        
        if (!localDraft) {
          console.warn('Aucun brouillon local trouvé pour afficher les différences');
          return;
        }
        
        // Fonction helper pour vérifier si un champ est "vide"
        const isEmpty = (value) => {
          if (value === null || value === undefined || value === '') return true;
          if (typeof value === 'string' && value.trim() === '') return true;
          if (typeof value === 'string' && (value === '<p><br></p>' || value === '<p></p>' || value === '<br>')) return true;
          if (typeof value === 'object' && Object.keys(value).length === 0) return true;
          return false;
        };
        
        // Fonction helper pour formater les valeurs pour l'affichage
        const formatValue = (value, fieldName) => {
          if (isEmpty(value)) return '';
          
          switch (fieldName) {
            case 'memo':
              // Pour les mémo Quill, traiter les contenus vides
              if (!value || value === '<p><br></p>' || value === '<p></p>' || value === '<br>') {
                return '';
              }
              // Pour les mémo Quill, afficher le texte sans HTML
              return value.replace(/<[^>]*>/g, '').trim() || value;
            default:
              return value;
          }
        };
        
        // Définir les champs à comparer
        const fieldsToCompare = [
          { field: 'title', fieldName: 'Titre', serverKey: 'title', localKey: 'title' },
          { field: 'client', fieldName: 'Client', serverKey: 'client', localKey: 'client' },
          { field: 'assigned_user_id', fieldName: 'Utilisateur assigné', serverKey: 'assigned_user_id', localKey: 'assigned_user_id' },
          { field: 'statut', fieldName: 'Statut', serverKey: 'statut', localKey: 'statut' },
          { field: 'memo', fieldName: 'Mémo', serverKey: 'memo', localKey: 'memo' }
        ];
        
        const differences = [];
        
        for (const field of fieldsToCompare) {
          const serverValue = data[field.serverKey];
          const localValue = localDraft[field.localKey];
          
          const serverEmpty = isEmpty(serverValue);
          const localEmpty = isEmpty(localValue);
          
          const serverValueFormatted = formatValue(serverValue, field.serverKey);
          const localValueFormatted = formatValue(localValue, field.localKey);
          
          // Ajouter la différence si les valeurs sont différentes
          if (serverValueFormatted !== localValueFormatted) {
            differences.push({
              field: field.field,
              fieldName: field.fieldName,
              serverValue: serverValueFormatted,
              localValue: localValueFormatted,
              serverEmpty: serverEmpty,
              localEmpty: localEmpty
            });
          }
        }
        
        // Mettre à jour les données de la modal
        this.diffData = {
          server: data,
          local: localDraft,
          differences: differences
        };
        
        console.log('🔍 Différences calculées:', differences);
        
        // Afficher la modal des différences
        this.showDiffModal = true;
      },

      // Gestionnaires pour la modal de synchronisation
      async handleSyncYes() {
        console.log('🔄 Synchronisation avec le serveur (patch automatique)...');
        this.showSyncModal = false;
        
        try {
          // Restaurer les données locales dans le formulaire
          if (this.syncModalData.localDraft) {
            console.log("📊 Données locales à restaurer:", this.syncModalData.localDraft);
            console.log("📊 Données serveur:", this.syncModalData.data);
            
            // Restaurer les données locales en gérant les champs vides de Quill
            const localDraft = this.syncModalData.localDraft;
            
            this.form.title = localDraft.title || '';
            this.form.client = localDraft.client;
            this.form.assigned_user_id = localDraft.assigned_user_id;
            this.form.statut = localDraft.statut || '';
            
            // Gérer le mémo Quill - convertir les contenus vides en chaîne vide
            if (localDraft.memo === '<p><br></p>' || localDraft.memo === '<p></p>' || localDraft.memo === '<br>' || !localDraft.memo || localDraft.memo.trim() === '') {
              this.form.memo = '';
            } else {
              this.form.memo = localDraft.memo;
            }
            
            this.form.photo_ids = [...localDraft.photo_ids];
            
            // Mettre à jour initialFormData pour qu'il corresponde aux données locales
            this.initialFormData = JSON.parse(JSON.stringify({
              title: this.form.title,
              client: this.form.client,
              assigned_user_id: this.form.assigned_user_id,
              statut: this.form.statut,
              memo: this.form.memo,
              photo_ids: [...this.form.photo_ids]
            }));
            
            console.log("📝 Données locales restaurées dans le formulaire et initialFormData mis à jour");
          }
          
          // Forcer un patch pour envoyer les modifications au serveur
          console.log("🔄 Patch forcé pour synchronisation...");
          
          try {
            // Envoyer les photos temporaires d'abord
            const newPhotoIds = [];
            if (this.tempPhotos.length > 0) {
              console.log("Envoi des photos temporaires...");
              for (const tempPhoto of this.tempPhotos) {
                try {
                  const { data } = await api.post('/photos-tasks', {
                    nom: tempPhoto.nom,
                    image: tempPhoto.image
                  });
                  newPhotoIds.push(data.id);
                } catch (e) {
                  console.error('Erreur envoi photo:', e);
                  throw new Error(`Erreur lors de l'envoi de la photo "${tempPhoto.nom}": ${e.message || 'Erreur serveur'}`);
                }
              }
            }

            // Combiner les IDs existants avec les nouveaux
            const allPhotoIds = [...this.form.photo_ids, ...newPhotoIds];

            // Créer le payload en gérant les champs vides de Quill
            const payload = {
              title: this.form.title,
              contact_id: this.form.client,
              assigned_user_id: this.form.assigned_user_id,
              statut: this.form.statut,
              photo_ids: allPhotoIds
            };
            
            // Gérer le mémo Quill - convertir les contenus vides en chaîne vide
            if (this.form.memo === '<p><br></p>' || this.form.memo === '<p></p>' || this.form.memo === '<br>' || !this.form.memo || this.form.memo.trim() === '') {
              payload.memo = '';
            } else {
              payload.memo = this.form.memo;
            }

            console.log("Envoi PATCH forcé pour synchronisation :", payload);

            await api.patch(`/tasks/${this.taskId}`, payload);
            
            // Marquer le patch comme fait
            sessionStorage.setItem(`task_patch_done_${this.taskId}`, 'true');
            
            // Vider les photos temporaires après sauvegarde réussie
            this.tempPhotos = [];
            
            // Nettoyer le brouillon des photos temporaires dans la DB locale
            try {
              await localDB.deleteTempTaskPhotos(this.taskId);
              console.log("📸 Brouillon des photos temporaires nettoyé après synchronisation");
            } catch (error) {
              console.error('❌ Erreur nettoyage brouillon photos:', error);
            }
            
            // Mettre à jour les données originales après sauvegarde réussie
            this.originalData = JSON.parse(JSON.stringify({
              title: this.form.title,
              client: this.form.client,
              assigned_user_id: this.form.assigned_user_id,
              statut: this.form.statut,
              memo: this.form.memo,
              photo_ids: allPhotoIds
            }));
            
            // Mettre à jour les données initiales pour la comparaison
            this.initialFormData = JSON.parse(JSON.stringify({
              title: this.form.title,
              client: this.form.client,
              assigned_user_id: this.form.assigned_user_id,
              statut: this.form.statut,
              memo: this.form.memo,
              photo_ids: allPhotoIds
            }));
            
            // Nettoyer les données locales après sauvegarde réussie
            try {
              console.log("🧹 Début du nettoyage des données locales...");
              await this.cleanLocalDataWithVerification('synchronisation réussie');
              console.log("✅ Synchronisation réussie, brouillon nettoyé");
            } catch (error) {
              console.error('Erreur nettoyage données locales:', error);
            }
            
          } catch (e) {
            console.error('Erreur lors du patch forcé:', e);
            const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la synchronisation';
            alert(`Erreur de synchronisation: ${errorMessage}`);
          }
          
          // Recharger la page pour afficher les données mises à jour
          console.log('🔄 Rechargement de la page...');
          window.location.reload();
        } catch (error) {
          console.error('Erreur lors de la synchronisation:', error);
        }
      },

      async handleSyncNoCancel() {
        console.log('❌ Annulation des modifications locales (utilisation des données serveur)...');
        this.showSyncModal = false;
        
        try {
          // Utiliser les données du serveur
          Object.assign(this.form, this.syncModalData.data);
          
          // Nettoyer les données locales
          await this.cleanLocalDataWithVerification('annulation des modifications');
          
          // Recharger la page pour afficher les données du serveur
          console.log('🔄 Rechargement de la page...');
          window.location.reload();
        } catch (error) {
          console.error('Erreur lors de l\'annulation:', error);
        }
      },

      async handleSyncNoContinue() {
        console.log('✏️ Continuation avec les modifications locales...');
        this.showSyncModal = false;
        
        // Continuer avec les données locales (déjà chargées)
        // Pas besoin de faire quoi que ce soit de plus
      },

      // Sauvegarde locale des modifications
      async saveLocalDraft() {
        if (!this.hasUnsavedChanges()) return;
        
        try {
          await localDB.saveTaskDraft(this.taskId, {
            title: this.form.title,
            client: this.form.client,
            assigned_user_id: this.form.assigned_user_id,
            statut: this.form.statut,
            memo: this.form.memo,
            photo_ids: [...this.form.photo_ids]
          });
          console.log('Brouillon local sauvegardé');
        } catch (error) {
          console.error('Erreur sauvegarde brouillon local:', error);
        }
      },

      // Sauvegarde des photos temporaires
      async saveTempPhotos() {
        if (this.tempPhotos.length === 0) return;
        
        try {
          const photosToSave = this.tempPhotos.map(photo => ({
            id: photo.id,
            name: photo.nom,
            data: photo.image
          }));
          
          await localDB.saveTempTaskPhotos(this.taskId, photosToSave);
          console.log('Photos temporaires sauvegardées localement');
        } catch (error) {
          console.error('Erreur sauvegarde photos temporaires:', error);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* Styles pour la recherche de clients */
  .client-search-results {
    max-height: 200px;
    max-width: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    position: relative;
    z-index: 1000;
    margin: 0 auto;
  }
  .client-option { transition: background-color 0.2s ease; }
  .client-option:hover { background-color: #f8f9fa !important; }
  .client-option.cursor-pointer { cursor: pointer; }
  .slider-wrapper { overflow:hidden; position:relative; }
  .slides { display:flex; }
  .slide {
  flex: 0 0 100%;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  padding:1rem;
  box-sizing:border-box;
}
  .fixed-footer {
    position:fixed; bottom:0; width:100%; background:#fff; padding:.5rem;
    box-shadow:0 -2px 5px rgba(0,0,0,0.1);
  }
  .pagination-dots { text-align:center; margin-bottom:.5rem; }
  .dot { display:inline-block; width:8px; height:8px; margin:0 4px;
    border-radius:50%; background:#ccc;
  }
  .dot.active { background:#007bff; }
  
  /* Indicateur d'envoi en cours */
  .uploading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.5rem;
    padding: 0.25rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  
  .actions { display:flex; justify-content:space-between; }
  .photo-thumbnail-wrapper { position:relative; }
  .delete-btn {
    position:absolute; top:2px; right:2px; width:22px; height:22px;
    background:#fff; border:none; border-radius:50%;
    color:#d00; font-weight:bold; box-shadow:0 0 3px #aaa; cursor:pointer;
  }
  .delete-btn:hover { background:#d00; color:#fff; }
  
  /* Centrer les champs sur PC */
  .form-group {
    text-align: center;
  }
  
  /* Centrer les champs de saisie */
  .form-control,
  .form-select,
  .custom-select {
    margin: 0 auto;
    text-align: center;
    max-width: 400px;
  }
  
  /* Ne pas centrer le texte dans les textarea (mémo) */
  .form-textarea,
  textarea {
    text-align: left;
  }
  
  /* Centrer les photos */
  .d-flex.flex-wrap {
    justify-content: center;
    text-align: center;
  }
  
  /* Styles pour Quill Editor */
  .quill-editor-container {
    max-width: 600px;
    margin: 0 auto;
  }
  
  .quill-editor {
    height: 300px;
    margin-bottom: 1rem;
  }
  
  /* Personnalisation de Quill */
  .ql-editor {
    min-height: 250px;
    font-size: 14px;
    line-height: 1.6;
  }
  
  .ql-toolbar {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: #f8f9fa;
  }
  
  .ql-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  
  /* Styles pour la modal des différences */
  .differences-container {
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .differences-header {
    border-bottom: 2px solid #dee2e6;
    padding-bottom: 1rem;
  }
  
  .differences-header h6 {
    margin: 0;
    font-weight: 600;
  }
  
  .difference-item {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    background-color: #f8f9fa;
  }
  
  .difference-field-name {
    margin-bottom: 0.75rem;
    color: #495057;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .server-value,
  .local-value {
    padding: 0.75rem;
    border-radius: 6px;
    min-height: 60px;
    word-wrap: break-word;
    white-space: pre-wrap;
    font-family: inherit;
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .server-value {
    background-color: #e3f2fd;
    border: 1px solid #bbdefb;
    color: #1565c0;
  }
  
  .local-value {
    background-color: #fff3e0;
    border: 1px solid #ffcc02;
    color: #e65100;
  }
  
  .empty-value {
    background-color: #f5f5f5 !important;
    border-color: #ddd !important;
    color: #999 !important;
    font-style: italic;
  }
  
  /* Responsive pour la modal des différences */
  @media (max-width: 768px) {
    .differences-container {
      max-height: 60vh;
    }
    
    .difference-item {
      padding: 0.75rem;
    }
    
    .server-value,
    .local-value {
      padding: 0.5rem;
      min-height: 50px;
      font-size: 0.85rem;
    }
    
    .difference-field-name {
      font-size: 0.8rem;
    }
  }
  
  /* Styles responsives pour mobile */
  @media (max-width: 600px) {
    .slide {
      padding-bottom: 120px;
    }
    
    /* Réduire la taille des champs sur mobile */
    .form-control,
    .form-select,
    .custom-select {
      max-width: 300px;
      margin: 0 auto;
    }
    
    /* Centrer les champs sur mobile */
    .form-group {
      text-align: center;
    }
    
    /* Réduire la taille des textarea sur mobile */
    .form-textarea {
      max-width: 300px;
      margin: 0 auto;
    }
    
    /* Ajuster Quill sur mobile */
    .quill-editor-container {
      max-width: 100%;
    }
    
    .quill-editor {
      height: 250px;
    }
    
    .ql-editor {
      min-height: 200px;
    }
  }
  </style>
  